import { SessionPut, SessionGet, SessionRemove } from './dataStorage.js'

// sessionStorage 键
const AUTH_PARAMS_KEY = 'AUTH_PARAMS'
const AUTH_INVALID_KEY = 'AUTH_INVALID'

// 防篡改参数判定字段（新版 AES-GCM 只需 sign；旧版 user_id/time 仍可透传，后端忽略）
// 若后端字段名不同（如 uid/timestamp/signature），仅改此处即可
export const REQUIRED_FIELDS = ['sign']

// 签名校验失败/过期的错误码（body.code 形式）；419 推荐，401 兜底
export const SIGN_INVALID_CODES = [419, 401]

// 验签失败友好文案
export const SIGN_INVALID_MSG = '活动链接已失效，请返回App重新进入'

// 路由内部字段，不参与透传
const ROUTE_INTERNAL_KEYS = ['tab']

/**
 * 判断 URL query 是否包含完整的签名参数（新版 AES-GCM 仅需 sign；旧版 HMAC 兼容需 user_id/time/sign）
 * 用于活动页面入口判定：签名参数齐全才允许进入
 * @param {Object} query 路由 query 对象
 * @returns {boolean}
 */
export function isAuthParamsComplete(query = {}) {
  const q = query || {}
  return REQUIRED_FIELDS.every(f => q[f] !== undefined && q[f] !== '')
}

/**
 * 从 $route.query 中挑出需要透传的参数（全量透传，仅排除路由内部字段）
 * @param {Object} query 路由 query 对象
 * @returns {Object} 可透传的参数对象
 */
export function pickAuthParams(query = {}) {
  const out = {}
  const q = query || {}
  Object.keys(q).forEach(k => {
    if (ROUTE_INTERNAL_KEYS.indexOf(k) === -1 && q[k] !== undefined && q[k] !== null && q[k] !== '') {
      out[k] = q[k]
    }
  })
  return out
}

/**
 * 写入防篡改参数到 sessionStorage
 * 仅当含签名参数（sign）时才写入，避免普通访问污染 session
 * @param {Object} query 路由 query 对象
 * @returns {Object} 实际写入的参数（未写入则返回空对象）
 */
export function setAuthParams(query = {}) {
  const params = pickAuthParams(query)
  const hasSign = REQUIRED_FIELDS.every(f => params[f] !== undefined && params[f] !== '')
  if (hasSign) {
    SessionPut(AUTH_PARAMS_KEY, params)
    // 新入口重置阻断标志
    SessionRemove(AUTH_INVALID_KEY)
    return params
  }
  return {}
}

/**
 * 读取 sessionStorage 中的防篡改参数
 * @returns {Object|null}
 */
export function getAuthParams() {
  return SessionGet(AUTH_PARAMS_KEY) || null
}

/**
 * 清理防篡改参数
 */
export function clearAuthParams() {
  SessionRemove(AUTH_PARAMS_KEY)
}

/**
 * 构造路由跳转用的 query 对象（透传全部活动参数）
 * 用于 goActivity/goBack，保证跳转后子页面 URL 仍带签名参数
 * 优先用当前页面 URL 的参数（最直接可靠），兜底用 sessionStorage
 * @param {Object} [currentQuery] 当前页面的 $route.query
 * @returns {Object}
 */
export function buildAuthQuery(currentQuery) {
  if (currentQuery && Object.keys(currentQuery).length) {
    return pickAuthParams(currentQuery)
  }
  const params = getAuthParams()
  return params ? Object.assign({}, params) : {}
}

/**
 * 把防篡改参数拼接到 URL query string
 * 用于 request.js 请求拦截器，自动注入到所有请求 URL
 * @param {string} url 原始 URL
 * @param {Object} params 防篡改参数
 * @returns {string} 拼接后的 URL
 */
export function appendAuthQuery(url, params) {
  if (!params) return url
  const parts = []
  Object.keys(params).forEach(k => {
    const v = params[k]
    if (v !== undefined && v !== null && v !== '') {
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v))
    }
  })
  if (!parts.length) return url
  return url + (url.indexOf('?') === -1 ? '?' : '&') + parts.join('&')
}

/**
 * 设置签名失效阻断标志
 * 设置后，request.js 请求拦截器会直接 reject，不再发送任何请求
 * @param {boolean} flag
 */
export function setAuthInvalid(flag) {
  SessionPut(AUTH_INVALID_KEY, !!flag)
}

/**
 * 读取签名失效阻断标志
 * @returns {boolean}
 */
export function isAuthInvalid() {
  return !!SessionGet(AUTH_INVALID_KEY)
}
